/**
 * @file BottleneckPanel.tsx
 * @description Panel listing the top operational bottlenecks:
 *              pending lab orders + flagged beds that need intervention.
 */

import { AlertTriangle, FlaskConical, BedDouble } from "lucide-react";
import { UnifiedRecord } from "../../types";

interface BottleneckPanelProps {
  records: UnifiedRecord[];
}

export function BottleneckPanel({ records }: BottleneckPanelProps) {
  // Bottleneck 1: Beds requiring action
  const flaggedBeds = records.filter(
    (r) =>
      r.has_conflict &&
      !r.is_orphaned_lab_record &&
      r.resolved_bed_status !== "Available"
  );

  // Bottleneck 2: STAT labs still pending
  const statPendingLabs = records.flatMap((r) =>
    r.lab_results
      .filter((l) => l.priority === "STAT" && l.result_status === "Pending")
      .map((l) => ({ ...l, patient_name: r.patient_name, patient_id: r.patient_id }))
  );

  // Bottleneck 3: Orphaned lab records
  const orphanedRecords = records.filter((r) => r.is_orphaned_lab_record);

  const hasNoBottlenecks =
    flaggedBeds.length === 0 &&
    statPendingLabs.length === 0 &&
    orphanedRecords.length === 0;

  return (
    <div
      className="rounded-2xl border border-white/8 p-6"
      style={{
        background: "linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.015) 100%)",
      }}
    >
      <div className="flex items-center gap-2 mb-5">
        <AlertTriangle className="w-4 h-4 text-amber-400" />
        <h2 className="text-white font-semibold text-base">Current Bottlenecks</h2>
      </div>

      {hasNoBottlenecks ? (
        <div className="text-center py-8 text-gray-600">
          <div className="text-2xl mb-2">✅</div>
          <p className="text-sm">No active bottlenecks detected</p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Flagged beds */}
          {flaggedBeds.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <BedDouble className="w-3.5 h-3.5 text-orange-400" />
                <span className="text-xs text-orange-400 font-semibold uppercase tracking-wide">
                  Beds Requiring Action ({flaggedBeds.length})
                </span>
              </div>
              <div className="space-y-2">
                {flaggedBeds.slice(0, 4).map((r) => (
                  <div
                    key={r.patient_id}
                    className="flex items-center justify-between px-3 py-2 rounded-lg bg-orange-500/8 border border-orange-500/20"
                  >
                    <div>
                      <span className="text-white text-xs font-medium">{r.resolved_bed}</span>
                      <span className="text-gray-500 text-xs ml-2">{r.patient_name}</span>
                    </div>
                    <span className="text-orange-400 text-xs">
                      {r.conflict_flags.filter((f) => f !== "Clean")[0]}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* STAT pending labs */}
          {statPendingLabs.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <FlaskConical className="w-3.5 h-3.5 text-red-400" />
                <span className="text-xs text-red-400 font-semibold uppercase tracking-wide">
                  STAT Labs Pending ({statPendingLabs.length})
                </span>
              </div>
              <div className="space-y-2">
                {statPendingLabs.slice(0, 4).map((l) => (
                  <div
                    key={l.order_id}
                    className="flex items-center justify-between px-3 py-2 rounded-lg bg-red-500/8 border border-red-500/20"
                  >
                    <div>
                      <span className="text-white text-xs font-medium">{l.test_name}</span>
                      <span className="text-gray-500 text-xs ml-2">{l.patient_name}</span>
                    </div>
                    <span className="text-red-400 text-xs font-mono">{l.order_id}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Orphaned records */}
          {orphanedRecords.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <FlaskConical className="w-3.5 h-3.5 text-purple-400" />
                <span className="text-xs text-purple-400 font-semibold uppercase tracking-wide">
                  Orphaned Lab Records ({orphanedRecords.length})
                </span>
              </div>
              <div className="space-y-2">
                {orphanedRecords.map((r) => (
                  <div
                    key={r.patient_id}
                    className="flex items-center justify-between px-3 py-2 rounded-lg bg-purple-500/8 border border-purple-500/20"
                  >
                    <span className="text-white text-xs font-medium">{r.patient_id}</span>
                    <span className="text-purple-400 text-xs">Manual review needed</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
